import React from 'react';
import './RunnerCv.css';

const PROFILE_SRC = '/images/profile.jpg';

const videoJobs = [
  {
    company: 'The Adare Collection',
    companyHref: 'https://theadarecollection.com/',
    location: null,
    badge: 'Remote',
    dates: 'Mar 2025 – present',
    title: 'Digital Designer',
    bullets: [
      'Designed and built the company website from scratch, including layout, UI and interactive property maps and virtual tours',
      'Create and refine digital assets for the live site, keeping visual design consistent across pages and features',
      'Work with the team on new features end to end, from concept and design through to launch on deadline',
      'Use AI-assisted workflows to speed up design and delivery',
    ],
  },
  {
    company: 'Truth Creative',
    companyHref: 'https://www.truth-creative.co.uk/',
    location: null,
    badge: 'Contract',
    dates: 'Jun 2025 – Feb 2026',
    title: 'Motion Designer & Videographer',
    groups: [
      {
        label: 'Filming & direction',
        bullets: [
          'Led small-crew branded shoots at client sites across the UK, owning the day from setup through wrap',
          'Set up lighting, camera and audio for client interviews, green screen capture and multicam shoots',
          'Conducted client interviews on camera and refined the script on shoot day',
        ],
      },
      {
        label: 'Edit & delivery',
        bullets: [
          'Edited raw footage to final delivery: multicam sync, cut to script and colour grade',
          'Keyed and composited green screen footage and built motion graphics into the final edits',
          'Ran client review rounds and turned around amends to deadline',
        ],
      },
    ],
  },
  {
    company: 'Box Bear',
    companyHref: 'https://boxbear.co.uk/',
    location: null,
    badge: 'In-house studio',
    dates: 'May 2022 – May 2024',
    title: 'Motion Graphic Designer & Production Assistant',
    groups: [
      {
        label: 'In the studio',
        bullets: [
          'Planned and filmed interviews with healthcare professionals for pharmaceutical clients',
          'Set up the in-house studio for shoots and live sessions, including cameras, lighting and audio',
          'Ran the multicamera and live-stream kit for virtual reality meetings, with healthcare professionals joining live from around the world',
        ],
      },
      {
        label: 'Edit & motion',
        bullets: [
          "Edited footage into informative videos shaped to each client's brief, from talking-head interviews to pieces led by 3D graphics",
          'Designed motion graphics in After Effects and 3D assets in Cinema 4D, assembled in Premiere Pro',
          'Responsible for organisation of media and versioning on each project through to final export',
        ],
      },
    ],
  },
  {
    company: 'Freelance',
    companyHref: 'https://uk.linkedin.com/in/seanderham',
    location: null,
    badge: 'Alongside employed roles',
    dates: 'Jun 2021 – May 2025',
    title: 'Motion Designer & Editor',
    bullets: [
      'Edited multicamera interviews and motion content for clients across sectors',
      'Took projects from offline edit through colour and graphics to finished deliverables',
    ],
  },
];

const skills = [
  'Camera, lighting and audio setup for interviews, multicam and green screen',
  'Directing presenters and contributors on shoot day; scripting and interview craft',
  'Premiere Pro and DaVinci Resolve: edit, multicam sync, colour grade and delivery',
  'After Effects and Cinema 4D: motion graphics and 3D for finished films',
  'End-to-end ownership: schedules, media organisation, client reviews and amends',
];

const JobEntry = ({ job }) => (
  <div className="runner-cv__job">
    <div className="runner-cv__job-top">
      <h3>
        {job.companyHref ? (
          <a href={job.companyHref} target="_blank" rel="noopener noreferrer">
            {job.company}
          </a>
        ) : (
          job.company
        )}
        {job.location ? (
          <span className="runner-cv__badge runner-cv__badge--quiet">
            {job.location}
          </span>
        ) : null}
        {job.badge ? <span className="runner-cv__badge">{job.badge}</span> : null}
      </h3>
      <span className="runner-cv__dates">{job.dates}</span>
    </div>
    <h4>{job.title}</h4>
    {job.groups ? (
      <div className="runner-cv__groups">
        {job.groups.map((group) => (
          <div className="runner-cv__group" key={group.label}>
            <p className="runner-cv__group-label">{group.label}</p>
            <ul className="runner-cv__bullets">
              {group.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ) : job.bullets ? (
      <ul className="runner-cv__bullets">
        {job.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : (
      <p>{job.body}</p>
    )}
  </div>
);

// Unlinked page: reachable only by typing the URL directly. Kept free of the
// site header and footer so it reads as a standalone CV.
const VideographyCv = () => {
  return (
    <div className="runner-cv-page">
      <article className="runner-cv">
        <header className="runner-cv__header">
          <div className="runner-cv__intro">
            <h1 className="runner-cv__name">Sean Derham</h1>
            <p className="runner-cv__role">Videographer</p>
            <p className="runner-cv__location">
              London · Available immediately · On-site
            </p>
            <p className="runner-cv__licence">Full UK driving licence</p>
            <div className="runner-cv__contacts runner-cv__contacts--screen">
              <a href="tel:+447597368482">07597 368482</a>
              <a href="mailto:sjderham@protonmail.com">sjderham@protonmail.com</a>
              <a
                href="https://seanderham.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                seanderham.com
              </a>
              <a
                href="https://seanderham.com/videography"
                target="_blank"
                rel="noopener noreferrer"
              >
                Videography
              </a>
              <a
                href="https://uk.linkedin.com/in/seanderham"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
            <div className="runner-cv__contacts runner-cv__contacts--print">
              <span>
                07597 368482 · sjderham@protonmail.com · seanderham.com ·
                linkedin.com/in/seanderham
              </span>
            </div>
          </div>
          <img
            className="runner-cv__photo"
            src={PROFILE_SRC}
            alt="Sean Derham"
            width={112}
            height={112}
          />
        </header>

        <section className="runner-cv__section">
          <h2>Profile</h2>
          <p>
            Videographer with four years owning video from concept through
            shoot and post. I set up camera, lighting and audio, direct and
            interview on camera, refine scripts on the day, then edit and grade
            the final cut, including motion graphics where the story needs them.
            Most of my work has been branded and educational-style content for
            clients, turning more complex briefings into clear films. I want a
            role where I can keep doing that end to end, for product education
            and digital platforms. Personal work lives on my Sony FX3, edited in
            Premiere Pro and graded in DaVinci Resolve.
          </p>
        </section>

        <section className="runner-cv__section" id="cv-work">
          <h2>Work Experience</h2>
          <div className="runner-cv__jobs">
            {videoJobs.map((job) => (
              <JobEntry key={`${job.company}-${job.dates}`} job={job} />
            ))}
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Personal Projects</h2>
          <p>
            I shoot, edit and grade short films on my own Sony FX3, including a
            personal travel film edited in Premiere Pro and graded in DaVinci
            Resolve. More at seanderham.com/videography.
          </p>
        </section>

        <section className="runner-cv__section">
          <h2>Skills &amp; Software</h2>
          <ul className="runner-cv__skill-list">
            {skills.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </section>

        <section className="runner-cv__section">
          <h2>Education</h2>
          <div className="runner-cv__job">
            <div className="runner-cv__job-top">
              <h3>University of Sussex</h3>
              <span className="runner-cv__dates">2018 – 2021</span>
            </div>
            <p>First Class Honours, Product Design</p>
          </div>
        </section>

        <p className="runner-cv__disclaimer">
          This CV may be kept on file and distributed for employment purposes.
        </p>
      </article>
    </div>
  );
};

export default VideographyCv;
